/**
 * ClienteImportFormatInfoModal - Modal with Excel format information for client import
 *
 * Shows the expected columns and format for bulk importing clients
 * Includes overwrite warning and required field indicators
 */
import React from 'react';
import { Info, X, AlertTriangle } from 'lucide-react';

interface ClienteImportFormatInfoModalProps {
  show: boolean;
  onClose: () => void;
}

export const ClienteImportFormatInfoModal: React.FC<ClienteImportFormatInfoModalProps> = ({
  show,
  onClose,
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Green */}
        <div className="flex items-center justify-between p-4 bg-green-600 rounded-t-lg flex-shrink-0">
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-white" />
            <h3 className="text-lg font-semibold text-white">
              Formato do Excel - Upload de Clientes
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-green-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 space-y-4 overflow-y-auto flex-grow">
          {/* Overwrite Warning */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-semibold text-amber-800 dark:text-amber-200">
                  Comportamento de Sobrescrita
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  Se um cliente com o <strong>mesmo CNPJ</strong> já existir,
                  os dados serão <strong>SOBRESCRITOS</strong> com os novos dados do Excel.
                </p>
              </div>
            </div>
          </div>

          {/* Required Fields Section */}
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
              Campos Obrigatórios (marcados com *)
            </p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400">A:</span>
                <span className="text-gray-800 dark:text-gray-200">Razao_Social*</span>
                <span className="text-xs text-gray-500">(Nome da empresa)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400">C:</span>
                <span className="text-gray-800 dark:text-gray-200">CNPJ*</span>
                <span className="text-xs text-gray-500">(14 dígitos)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400">D:</span>
                <span className="text-gray-800 dark:text-gray-200">Email*</span>
                <span className="text-xs text-gray-500">(formato válido)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-600 dark:text-gray-400">E/F:</span>
                <span className="text-gray-800 dark:text-gray-200">Regime*</span>
                <span className="text-xs text-gray-500">(pelo menos 1)</span>
              </div>
            </div>
          </div>

          {/* Columns Section */}
          <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              Todas as Colunas do Excel (A-Z)
            </p>

            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {/* Column A-M */}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-red-500 w-5">A:</span>
                  <span className="text-gray-800 dark:text-gray-200">Razao_Social*</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">B:</span>
                  <span className="text-gray-800 dark:text-gray-200">Nome_Fantasia</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-red-500 w-5">C:</span>
                  <span className="text-gray-800 dark:text-gray-200">CNPJ*</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-red-500 w-5">D:</span>
                  <span className="text-gray-800 dark:text-gray-200">Email*</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-red-500 w-5">E:</span>
                  <span className="text-gray-800 dark:text-gray-200">Com_Regime*</span>
                  <span className="text-xs text-gray-500">(TRUE/FALSE)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-red-500 w-5">F:</span>
                  <span className="text-gray-800 dark:text-gray-200">Sem_Regime*</span>
                  <span className="text-xs text-gray-500">(TRUE/FALSE)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">G:</span>
                  <span className="text-gray-800 dark:text-gray-200">Telefone</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">H:</span>
                  <span className="text-gray-800 dark:text-gray-200">CEP</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">I:</span>
                  <span className="text-gray-800 dark:text-gray-200">Endereco</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">J:</span>
                  <span className="text-gray-800 dark:text-gray-200">Bairro</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">K:</span>
                  <span className="text-gray-800 dark:text-gray-200">Cidade</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">L:</span>
                  <span className="text-gray-800 dark:text-gray-200">Estado</span>
                  <span className="text-xs text-gray-500">(UF)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">M:</span>
                  <span className="text-gray-800 dark:text-gray-200">Periodo_Segregacao</span>
                </div>
              </div>

              {/* Column N-Z */}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">N:</span>
                  <span className="text-gray-800 dark:text-gray-200">Sinalizar_Vencimento_1</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">O:</span>
                  <span className="text-gray-800 dark:text-gray-200">Sinalizar_Vencimento_2</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">P:</span>
                  <span className="text-gray-800 dark:text-gray-200">Sinalizar_Vencimento_3</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">Q:</span>
                  <span className="text-gray-800 dark:text-gray-200">Input_Saldos_Via_Lotes</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">R:</span>
                  <span className="text-gray-800 dark:text-gray-200">Sinalizacao_Auditiva</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">S:</span>
                  <span className="text-gray-800 dark:text-gray-200">Intervalo_Venc_Min</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">T:</span>
                  <span className="text-gray-800 dark:text-gray-200">Intervalo_Venc_Max</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">U:</span>
                  <span className="text-gray-800 dark:text-gray-200">Msg_Log_Beebagem</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">V:</span>
                  <span className="text-gray-800 dark:text-gray-200">SMTP_Servidor</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">W:</span>
                  <span className="text-gray-800 dark:text-gray-200">SMTP_Porta</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">X:</span>
                  <span className="text-gray-800 dark:text-gray-200">SMTP_TLS</span>
                  <span className="text-xs text-gray-500">(TRUE/FALSE)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">Y:</span>
                  <span className="text-gray-800 dark:text-gray-200">Email_Copia</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-600 dark:text-gray-400 w-5">Z:</span>
                  <span className="text-gray-800 dark:text-gray-200">Ativo</span>
                  <span className="text-xs text-gray-500">(TRUE/FALSE)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Validation Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
              Validações Aplicadas:
            </p>
            <ul className="text-xs text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
              <li><strong>CNPJ:</strong> Formato (14 dígitos) + dígitos verificadores</li>
              <li><strong>Email:</strong> Formato válido (exemplo@dominio.com)</li>
              <li><strong>Regime:</strong> Pelo menos Com_Regime OU Sem_Regime deve ser TRUE</li>
            </ul>
          </div>

          {/* Legend */}
          <div className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-3">
            <p><span className="text-red-500 font-bold">*</span> Campos obrigatórios</p>
            <p className="mt-1">Os campos booleanos aceitam: TRUE, FALSE, SIM, NAO, 1, 0</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};
